"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type AccordionItemData = {
  id: string;
  trigger: React.ReactNode;
  content: React.ReactNode;
};

type AccordionProps = {
  items: AccordionItemData[];
  type?: "single" | "multiple";
  className?: string;
  itemClassName?: (isOpen: boolean) => string;
};

export function Accordion({ items, type = "single", className, itemClassName }: AccordionProps) {
  const [openItems, setOpenItems] = React.useState<Set<string>>(new Set());

  function toggleItem(id: string) {
    setOpenItems((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (type === "single") {
          next.clear();
        }
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className={cn("space-y-2", className)}>
      {items.map((item) => {
        const isOpen = openItems.has(item.id);

        return (
            <div
              key={item.id}
              className={cn(
                "group rounded-2xl border transition-[transform,box-shadow,border-color] duration-300",
                isOpen 
                  ? "border-emerald-500/20 bg-emerald-50/10 shadow-[0_4px_20px_rgba(16,185,129,0.05)]" 
                  : "border-black/5 bg-white hover:border-black/10 hover:shadow-sm",
                itemClassName?.(isOpen),
              )}
            >
              <button
                type="button"
                id={`accordion-trigger-${item.id}`}
                aria-expanded={isOpen}
                aria-controls={`accordion-content-${item.id}`}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-bold text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/50"
                onClick={() => toggleItem(item.id)}
              >
                <span className="flex-1 text-base md:text-lg tracking-tight">{item.trigger}</span>
                <div className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border border-black/5 bg-white shadow-sm transition-[transform,box-shadow,border-color] duration-300",
                  isOpen && "border-emerald-500/20 rotate-180"
                )}>
                  <ChevronDown
                    size={16}
                    aria-hidden="true"
                    className={cn(
                      "text-slate-500 transition-colors",
                      isOpen && "text-emerald-700"
                    )}
                  />
                </div>
              </button>
              <div
                id={`accordion-content-${item.id}`}
                role="region"
                aria-labelledby={`accordion-trigger-${item.id}`}
                className={cn(
                  "grid transition-all duration-300 ease-in-out",
                  isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                )}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-6 text-slate-600 font-medium leading-relaxed max-w-3xl">
                    {item.content}
                  </div>
                </div>
              </div>
            </div>
        );
      })}
    </div>
  );
}
